import { useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router";
import { api, ApiError, upload } from "../lib/api";
import { useI18n } from "../lib/i18n";
import type { MessageKey } from "../lib/messages";
import { useSession } from "../lib/session";
import { useAppConfig } from "../lib/appConfig";
import { useAsync } from "../lib/useAsync";
import { IconCamera, IconNext } from "../ui/icons";
import { AsyncButton, Empty, ErrorState, Field, Loading, Notice, PageHeader, useToast } from "../ui/kit";

// Help requests: problems with a deal (the other person can read and reply) and questions to the Walashy team.

// What a buyer can report about the phone, and what either side can report about the other person.
const BUYER_REASONS = ["not_as_described", "locked", "not_genuine", "damaged", "not_received", "no_show", "other"] as const;
const SELLER_REASONS = ["no_show", "other"] as const;
const QUESTION_REASONS = ["safety", "account", "selling", "other"] as const;

type Ticket = {
  id: string; refNo: number; kind: "deal_issue" | "question"; reason: string; subject: string; status: "open" | "resolved" | "closed";
  // "customer" opened the request; "seller" is the other person in the deal (either side can open one).
  resolution: string | null; createdAt: string; role: "customer" | "seller" | "staff"; customerName: string | null; otherName: string | null;
  deal: { id: string; status: string; title: string; quantity: number; priceCents: number; currency: string; openedBy: "buyer" | "seller" } | null;
  messages: { id: string; authorRole: "customer" | "seller" | "staff" | "system"; authorName: string | null; body: string; createdAt: string }[];
  photos: { id: string; url: string; createdAt: string }[];
};

/** Either person in a deal: report a problem with it. Photos can be added on the next screen. */
export function ReportProblem() {
  const { id } = useParams();
  const { t } = useI18n();
  const nav = useNavigate();
  const { me, loading } = useSession();
  const deal = useAsync(
    () => api<{ conversation: { listingTitle: string; role: "buyer" | "seller"; otherName: string }; deal: { canReport: boolean; openIssue: { id: string } | null } }>(`/api/conversations/${id}/messages`),
    [id],
  );
  const [reason, setReason] = useState<string>("");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  if (!loading && !me) return <Navigate to={`/signin?next=/inbox/${id}/problem`} replace />;
  if (deal.loading && !deal.data) return <Loading />;
  if (deal.error || !deal.data) return <ErrorState error={deal.error} onRetry={deal.reload} />;
  if (deal.data.deal.openIssue) return <Navigate to={`/support/${deal.data.deal.openIssue.id}`} replace />;
  const { conversation: c } = deal.data;
  const reasons = c.role === "buyer" ? BUYER_REASONS : SELLER_REASONS;

  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }} className="stack-lg">
      <PageHeader title={t("support.reportTitle")} back={`/inbox/${id}`} />
      <p className="muted" style={{ margin: 0 }} dir="auto">{c.listingTitle} · {c.otherName}</p>
      {!deal.data.deal.canReport ? (
        <Notice kind="warn">{t("support.reportClosed")} <Link to="/contact">{t("info.contact")}</Link></Notice>
      ) : (
        <>
          <Notice>{t("support.reportHint")}</Notice>
          <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend style={{ fontWeight: 650, marginBottom: "var(--s2)" }}>{t("support.whatsWrong")}</legend>
            {reasons.map((r) => (
              <label key={r} className="choice" style={{ flexDirection: "row", alignItems: "center", gap: "var(--s3)" }}>
                <input type="radio" name="reason" value={r} checked={reason === r} onChange={() => setReason(r)} style={{ width: 20, height: 20 }} />
                <span>{t(`support.reason.${r}` as MessageKey)}</span>
              </label>
            ))}
          </fieldset>
          <Field label={t("support.details")} hint={t("support.detailsHint")} error={error}>
            {(fid, d) => <textarea id={fid} className="input" rows={5} value={body} onChange={(e) => setBody(e.target.value)} maxLength={2000} dir="auto" aria-describedby={d} />}
          </Field>
          <div className="sticky-cta">
            <AsyncButton
              className="btn primary block"
              disabled={!reason || body.trim().length < 10}
              onClick={async () => {
                setError(null);
                try {
                  const r = await api<{ id: string }>("/api/support", { body: { kind: "deal_issue", conversationId: id, reason, body } });
                  nav(`/support/${r.id}?new=1`, { replace: true });
                } catch (e) {
                  setError((e as ApiError).message);
                }
              }}
            >
              {t("support.send")}
            </AsyncButton>
          </div>
        </>
      )}
    </div>
  );
}

/** "Chat with us on WhatsApp", when the store has a support number set (SUPPORT_WHATSAPP). */
export function WhatsAppSupport({ text }: { text?: string }) {
  const { t } = useI18n();
  const { supportWhatsapp } = useAppConfig();
  if (!supportWhatsapp) return null;
  const href = `https://wa.me/${supportWhatsapp.replace(/\D/g, "")}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
  return <a className="btn block whatsapp" href={href} target="_blank" rel="noreferrer">{t("support.whatsapp")}</a>;
}

/** Signed-in contact form: a question to the Walashy team. */
export function AskSupport() {
  const { t } = useI18n();
  const nav = useNavigate();
  const { me } = useSession();
  const [reason, setReason] = useState<string>("other");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  if (!me)
    return (
      <div className="card stack">
        <strong>{t("support.askTitle")}</strong>
        <p className="small muted" style={{ margin: 0 }}>{t("support.signInToAsk")}</p>
        <Link className="btn primary" to="/signin?next=/contact">{t("common.signInToContinue")}</Link>
      </div>
    );
  return (
    <section className="card stack" aria-labelledby="ask-title">
      <strong id="ask-title">{t("support.askTitle")}</strong>
      <div className="chips" role="radiogroup" aria-label={t("support.topic")}>
        {QUESTION_REASONS.map((r) => (
          <button key={r} type="button" role="radio" aria-checked={reason === r} className="chip" aria-pressed={reason === r} onClick={() => setReason(r)}>
            {t(`support.reason.${r}` as MessageKey)}
          </button>
        ))}
      </div>
      <Field label={t("support.subject")}>
        {(fid) => <input id={fid} className="input" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={120} dir="auto" />}
      </Field>
      <Field label={t("support.message")} error={error}>
        {(fid) => <textarea id={fid} className="input" rows={5} value={body} onChange={(e) => setBody(e.target.value)} maxLength={2000} dir="auto" />}
      </Field>
      <AsyncButton
        className="btn primary block"
        disabled={subject.trim().length < 3 || body.trim().length < 10}
        onClick={async () => {
          setError(null);
          try {
            const r = await api<{ id: string }>("/api/support", { body: { kind: "question", reason, subject, body } });
            nav(`/support/${r.id}`);
          } catch (e) {
            setError((e as ApiError).message);
          }
        }}
      >
        {t("support.send")}
      </AsyncButton>
    </section>
  );
}

const tone = (s: string) => (s === "open" ? "offer" : s === "resolved" ? "good" : "");

export function SupportList() {
  const { t, date } = useI18n();
  const { me, loading: sLoading } = useSession();
  const { data, error, loading, reload } = useAsync(() => (me ? api<{ tickets: (Omit<Ticket, "messages" | "photos" | "deal"> & { conversationId: string | null; lastMessageAt: string; lastAuthor: string })[] }>("/api/support") : Promise.resolve({ tickets: [] })), [me?.id]);
  if (!sLoading && !me) return <Navigate to="/signin?next=/support" replace />;
  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      <PageHeader title={t("support.title")} back="/account" />
      {loading && !data ? <Loading /> : error ? <ErrorState error={error} onRetry={reload} /> : !data!.tickets.length ? (
        <Empty title={t("support.empty")} body={t("support.emptyBody")} action={<Link className="btn" to="/help">{t("info.help")}</Link>} />
      ) : (
        <div className="list">
          {data!.tickets.map((tk) => (
            <Link key={tk.id} to={`/support/${tk.id}`} className="list-item">
              <div className="grow">
                <div className="t" dir="auto">#{tk.refNo} · {tk.subject || t(`support.reason.${tk.reason}` as MessageKey)}</div>
                <div className="s">
                  <span className={`tag ${tone(tk.status)}`}>{t(`support.status.${tk.status}` as MessageKey)}</span>{" "}
                  {tk.role === "seller" ? t("support.onYourDeal") : t(`support.reason.${tk.reason}` as MessageKey)} · {date(tk.lastMessageAt)}
                </div>
              </div>
              {tk.status === "open" && tk.lastAuthor === "staff" && <span className="badge-dot" style={{ position: "static" }}>1</span>}
              <IconNext size={18} className="muted" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function SupportThread() {
  const { id } = useParams();
  const { t, date, money } = useI18n();
  const toast = useToast();
  const { data: tk, error, loading, reload, setData } = useAsync(() => api<Ticket>(`/api/support/${id}`), [id]);
  const [reply, setReply] = useState("");
  const [sendError, setSendError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  if (loading && !tk) return <Loading />;
  if (error || !tk) return <ErrorState error={error} onRetry={reload} />;
  const fresh = new URLSearchParams(location.search).has("new");
  const who = (m: Ticket["messages"][number]) =>
    m.authorRole === "staff" ? t("support.team") : m.authorRole === "system" ? "" : m.authorRole === tk.role ? t("support.you") : (m.authorName ?? "");
  const canWrite = tk.status === "open";

  return (
    <div style={{ maxWidth: 680, margin: "0 auto" }} className="stack-lg">
      <PageHeader title={`${t("support.request")} #${tk.refNo}`} back="/support" />
      {fresh && <Notice kind="good">{t("support.sent")}</Notice>}
      <section className="card stack">
        <div className="row between">
          <span className={`tag ${tone(tk.status)}`}>{t(`support.status.${tk.status}` as MessageKey)}</span>
          <span className="small muted">{date(tk.createdAt)}</span>
        </div>
        <strong dir="auto">{tk.subject || t(`support.reason.${tk.reason}` as MessageKey)}</strong>
        <div className="small muted">{t(`support.reason.${tk.reason}` as MessageKey)}</div>
        {tk.deal && (
          <Link to={`/inbox/${tk.deal.id}`} className="small" dir="auto">
            {t("support.deal")}: {tk.deal.quantity > 1 ? `${tk.deal.quantity} × ` : ""}{tk.deal.title} · {money(tk.deal.priceCents * tk.deal.quantity, tk.deal.currency)}
          </Link>
        )}
        {tk.role === "seller" && <Notice>{t("support.otherHint", { name: tk.customerName ?? "" })}</Notice>}
        {tk.resolution && tk.status !== "open" && <Notice kind="good">{tk.resolution}</Notice>}
      </section>

      <ol className="support-thread" aria-label={t("support.conversation")}>
        {tk.messages.map((m) => (
          <li key={m.id} className={`msg-${m.authorRole}${m.authorRole === tk.role ? " mine" : ""}`}>
            {m.authorRole !== "system" && <div className="tiny muted">{who(m)} · {date(m.createdAt, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</div>}
            <div className="bubble" dir="auto">{m.body}</div>
          </li>
        ))}
      </ol>

      {tk.photos.length > 0 && (
        <div className="thumbs" aria-label={t("support.photos")}>
          {tk.photos.map((p) => <a key={p.id} href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt={t("support.photo")} /></a>)}
        </div>
      )}

      {canWrite && (
        <section className="stack">
          <Field label={t("support.reply")} error={sendError}>
            {(fid) => <textarea id={fid} className="input" rows={3} value={reply} onChange={(e) => setReply(e.target.value)} maxLength={2000} dir="auto" />}
          </Field>
          <div className="row" style={{ gap: "var(--s2)", flexWrap: "wrap" }}>
            <AsyncButton
              className="btn primary"
              disabled={!reply.trim()}
              onClick={async () => {
                setSendError(null);
                try {
                  setData(await api<Ticket>(`/api/support/${tk.id}/messages`, { body: { body: reply } }));
                  setReply("");
                } catch (e) {
                  setSendError((e as ApiError).message);
                }
              }}
            >
              {t("support.sendReply")}
            </AsyncButton>
            {tk.role !== "staff" && tk.photos.length < 6 && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (!f) return;
                    try {
                      setData(await upload<Ticket>(`/api/support/${tk.id}/photos`, f));
                    } catch (err) {
                      toast((err as ApiError).message);
                    }
                  }}
                />
                <button type="button" className="btn" onClick={() => fileRef.current?.click()}><IconCamera size={18} /> {t("support.addPhoto")}</button>
              </>
            )}
          </div>
          {tk.role === "customer" && (
            <AsyncButton className="btn ghost sm" onClick={async () => { await api(`/api/support/${tk.id}/close`, { body: {} }); await reload(); }}>
              {t("support.closeIt")}
            </AsyncButton>
          )}
        </section>
      )}
      {!canWrite && tk.role !== "staff" && <p className="small muted">{t("support.closedHint")} <Link to="/contact">{t("info.contact")}</Link></p>}
    </div>
  );
}
